import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MapPin, Bed, Bath, Car, Loader2, MessageCircle, Info } from 'lucide-react';
import { useState, useCallback, memo } from 'react';

import { PropertyType } from '../../types/property';
import { getPropertyCardImageSrc, handleImageError, isS3Image } from '../../lib/imageUtils';
import { formatDate } from '../../lib/format';
import { useSavedProperties } from '../../hooks/useSavedProperties';
import { useUserStore } from '../../stores/userStore';
import ContactAgentModal from './ContactAgentModal';

interface PropertyCardProps {
  property: PropertyType;
  showSaveButton?: boolean; // Allow hiding the save button if needed
}

const PropertyCard = memo(({ property, showSaveButton = true }: PropertyCardProps) => {
  const { t } = useTranslation();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  // Get user from store for debugging
  const { user } = useUserStore();
  
  // Use the saved properties hook
  const { isPropertySaved, togglePropertySaved, savingProperty } = useSavedProperties();
  
  // Add safety checks for required property fields
  if (!property || !property.id || !property.title) {
    console.error('PropertyCard: Invalid property data received:', property);
    return (
      <div className="card p-4 text-center text-red-600">
        <p>Invalid property data</p>
      </div>
    );
  }
  
  // Ensure images array is always defined
  const safeImages = Array.isArray(property.images) ? property.images : [];
  
  // Check if this property is currently saved
  const isSaved = isPropertySaved(property.id);
  const isSaving = savingProperty === property.id;

  const handleSaveToggle = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user?.id) {
      console.log('🔍 PropertyCard: No user, cannot save property');
      return;
    }
    
    try {
      await togglePropertySaved(property.id);
    } catch (error) {
      console.error('🔍 PropertyCard: Error toggling property saved:', error);
    }
  }, [user?.id, togglePropertySaved, property.id]);

  const handleContactClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsContactModalOpen(true);
  }, []);

  // Format price with proper currency
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'sold':
        return 'bg-blue-100 text-blue-800';
      case 'rented':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return t('property.status.active', 'Active');
      case 'draft':
        return t('property.status.draft', 'Draft');
      case 'pending':
        return t('property.status.pending', 'Pending');
      case 'sold':
        return t('property.status.sold', 'Sold');
      case 'rented':
        return t('property.status.rented', 'Rented');
      default:
        return status;
    }
  };

  return (
    <>
      <article className="card group min-h-[400px] flex flex-col" 
               role="article" 
               aria-labelledby={`property-title-${property.id}`}>
        <Link 
          to={`/properties/${property.id}`} 
          className="block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg flex-1 flex flex-col"
          aria-describedby={`property-details-${property.id}`}>
          <div className="relative flex-shrink-0">
            {/* Status Badge - REMOVED */}
            {/* {property.status && (
              <div className="absolute top-3 left-3 z-10">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
                  {getStatusLabel(property.status)}
                </span>
              </div>
            )} */}

            {/* Image Container with aspect ratio preservation to prevent layout shift */}
            <div className="h-48 w-full bg-neutral-100 relative overflow-hidden rounded-t-lg">
              {/* Always show a placeholder to maintain layout */}
              <div className="absolute inset-0 bg-neutral-100" />
              
              {imageLoading && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              )}
              
              {/* Always render the image element, falling back to placeholder */}
              <img
                src={
                  imageError || !safeImages.length
                    ? '/placeholder-property.svg'
                    : getPropertyCardImageSrc(safeImages)
                }
                alt={property.title}
                width={480}
                height={270}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  imageLoading ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={() => setImageLoading(false)}
                onError={(e) => {
                  handleImageError(e.nativeEvent);
                  setImageError(true);
                  setImageLoading(false);
                }}
                loading="lazy"
                decoding="async"
              />

              {/* S3 Storage Indicator - only show for valid S3 images */}
              {!imageError && safeImages.length > 0 && isS3Image(safeImages?.[0]) && (
                <div className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded font-medium"
                     aria-label="S3 Storage Image">
                  S3
                </div>
              )}

              {/* Property Status Badge - REMOVED */}
              {/* {property.status && (
                <div className={`absolute top-2 right-16 px-2 py-1 rounded text-xs font-medium ${getStatusColor(property.status)}`}>
                  {property.status}
                </div>
              )} */}
            </div>
            
            {/* Save/Favorite Button */}
            {showSaveButton && (
              <button
                onClick={handleSaveToggle}
                className={`absolute top-2 right-2 p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                  !user?.id 
                    ? 'bg-neutral-100 hover:bg-neutral-200' 
                    : 'bg-white bg-opacity-90 hover:bg-opacity-100'
                }`}
                aria-label={isSaved ? 'Remove from saved properties' : 'Add to saved properties'}
                aria-pressed={isSaved}
                disabled={isSaving}
                title={!user?.id ? 'Sign in to save properties' : (isSaved ? 'Remove from saved properties' : 'Add to saved properties')}
              >
                {isSaving ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
                ) : (
                  <Heart 
                    className={`h-5 w-5 ${
                      isSaved ? 'fill-error-500 text-error-500' : 
                      !user?.id ? 'text-neutral-400' : 'text-neutral-400'
                    }`} 
                    aria-hidden="true"
                  />
                )}
              </button>
            )}
            
            {/* Authentication Status Indicator (for debugging) */}
            {process.env.NODE_ENV === 'development' && (
              <div className="absolute top-2 left-2 text-xs bg-blue-500 text-white px-2 py-1 rounded opacity-75">
                {user?.id ? `User: ${user.id.substring(0, 8)}...` : 'No User'}
              </div>
            )}
            
            {property.featured && (
              <div className="absolute top-2 left-2 bg-accent-500 text-white text-xs px-2 py-1 rounded font-medium"
                   aria-label="Featured Property">
                {t('property.featured', 'Featured')}
              </div>
            )}
          </div>
          
          <div className="p-4 flex-1 flex flex-col" id={`property-details-${property.id}`}>
            <div className="flex justify-between items-start mb-2">
              <p className="text-primary-600 font-semibold text-lg">
                {formatPrice(property.price)}
                <span className="text-sm text-neutral-500 font-normal block">/month</span>
              </p>
              {property.deposit && (
                <span className="text-xs bg-neutral-100 px-2 py-1 rounded text-neutral-600">
                  {t('property.deposit', 'Deposit Required')}
                </span>
              )}
            </div>
            
            <h3 
              id={`property-title-${property.id}`}
              className="mt-2 font-bold text-neutral-800 group-hover:text-primary-700 line-clamp-2"
            >
              {property.title}
            </h3>
            
            <div className="mt-1 flex items-center text-neutral-500 text-sm">
              <MapPin className="h-3.5 w-3.5 mr-1 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">
                {property.location?.city || 'Unknown City'}, {property.location?.region || 'Unknown Region'}
              </span>
            </div>
            
            <div className="mt-3 flex items-center space-x-4 text-neutral-600">
              <div className="flex items-center">
                <Bed className="h-4 w-4 mr-1 flex-shrink-0" aria-hidden="true" />
                <span>{property.bedrooms || 0} {t('property.bedrooms', 'bed')}</span>
              </div>
              <div className="flex items-center">
                <Bath className="h-4 w-4 mr-1 flex-shrink-0" aria-hidden="true" />
                <span>{property.bathrooms || 0} {t('property.bathrooms', 'bath')}</span>
              </div>
              {(property.parking || 0) > 0 && (
                <div className="flex items-center">
                  <Car className="h-4 w-4 mr-1 flex-shrink-0" aria-hidden="true" />
                  <span>{property.parking || 0} {t('property.parking', 'parking')}</span>
                </div>
              )}
            </div>
            
            <div className="mt-3 text-sm">
              <span className="text-neutral-500">
                {property.availableFrom === 'now' 
                  ? t('property.availableNow', 'Available Now') 
                  : t('property.available', 'Available from {{date}}', { 
                      date: property.availableFrom ? formatDate(property.availableFrom) : 'TBD' 
                    })}
              </span>
            </div>

            {/* Property Type Badge */}
            <div className="mt-3">
              <span className="inline-block bg-neutral-100 text-neutral-600 text-xs px-2 py-1 rounded">
                {property.propertyType}
              </span>
            </div>

            {/* Property Source Indicator */}
            {property.owner_id && (
              <div className="mt-2">
                <span className={`inline-block text-xs px-2 py-1 rounded ${
                  property.agent?.agency === 'Property Owner' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {property.agent?.agency === 'Property Owner' ? '🏠 Landlord' : '👨‍💼 Agent'}
                </span>
              </div>
            )}

            {/* Spacer to push contact button to bottom */}
            <div className="flex-1" />

            {/* Guest access hint – show only when not signed in */}
            {!user?.id && (
              <div
                className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-primary-50 border border-primary-100"
                role="status"
                aria-label={t('property.guestSignInHint', 'Sign in to contact the owner or apply for this property.')}
              >
                <Info className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-primary-800">
                  {t('property.guestSignInHint', 'Sign in to contact the owner or apply for this property.')}
                </p>
              </div>
            )}

            {/* Contact Button */}
            <div className="mt-4 pt-3 border-t border-neutral-200">
              <button
                onClick={handleContactClick}
                className="w-full btn-primary flex items-center justify-center gap-2 py-2 text-sm"
                title={!user?.id ? t('property.contactButtonTooltipGuest', 'Sign in to contact or apply') : t('property.contact', 'Contact')}
              >
                <MessageCircle className="h-4 w-4" />
                {t('property.contact', 'Contact')}
              </button>
            </div>
          </div>
        </Link>
      </article>

      {/* Contact Modal */}
      <ContactAgentModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        agentName={property.agent?.name || "Property Contact"}
        agentEmail={property.agent?.email || "contact@platform.com"}
        propertyTitle={property.title}
        propertyLink={`${window.location.origin}/properties/${property.id}`}
        propertyId={property.id}
        inquiryType="message"
      />
    </>
  );
});

PropertyCard.displayName = 'PropertyCard';

export default PropertyCard;